import { actions } from "@app/utils";
import { createApiClient } from "@app/utils/apiClient";
// import { IMS } from "./imsService";

export const demoPilotClient = createApiClient(
  actions["exd-storyteller-ai/zoltarDemoPilot"],
  5,
  0,
);

/**
 * Scrapes content from a given URL.
 *
 * @param {string} url - The URL to scrape.
 * @param {Object} params - Scraping parameters.
 * @param {boolean} params.colors - Whether to extract colors.
 * @param {boolean} params.images - Whether to extract images.
 * @param {boolean} params.fonts - Whether to extract fonts.
 * @param {boolean} params.texts - Whether to extract texts.
 * @param {number} params.minPx - Minimum image size.
 * @param {number} params.maxPx - Maximum image size.
 * @returns {Promise<Object>} A promise that resolves to the scraped data.
 * @throws {Error} If the request fails.
 */
export const scrape = async (
  url,
  {
    brandColors = true,
    colorFormat = "hex",
    colors = true,
    images = true,
    texts = false,
    minPx = 64,
    maxPx = 4096,
  } = {},
  options = {},
) => {
  const response = await demoPilotClient.get(
    "/api/assets",
    {
      url,
      brandColors,
      colorFormat,
      colors,
      images,
      texts,
      minPx,
      maxPx,
    },
    options,
  );

  if (!response.ok) {
    throw new Error("Failed to scrape URL", { cause: response });
  }

  return response.data;
};

/**
 * Gets an account by name.
 *
 * @param {string} accountName - The account name to search for.
 * @returns {Promise<Array<string>>} A promise that resolves to an array of account names.
 * @throws {Error} If the request fails.
 */
export const getAccounts = async (accountName, options = {}) => {
  // FIXME: Remove this temporary check once the upstream service is fixed.
  return [];

  const response = await demoPilotClient.get(
    "/accounts",
    { accountName },
    options,
  );

  if (!response.ok) {
    throw new Error("Failed to get accounts", { cause: response });
  }

  return response.data.map((name) => ({ id: name, name }));
};

/**
 * Gets a DR number by name.
 *
 * @param {string} drNumber - The DR number to search for.
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of DR number objects.
 * @throws {Error} If the request fails.
 */
export const getDrNumbers = async (drNumber, options = {}) => {
  // FIXME: Remove this temporary check once the upstream service is fixed.
  return [];

  const response = await demoPilotClient.get(
    "/dr-numbers",
    { drNumber },
    options,
  );

  if (!response.ok) {
    throw new Error("Failed to get DR numbers", { cause: response });
  }

  return response.data;
};

/**
 * Initializes a multipart upload.
 *
 * @param {Object} options - Upload information.
 * @param {string} options.fileName - The file name.
 * @param {string} options.fileType - The file MIME type.
 * @param {number} options.fileSize - The file size in bytes.
 * @param {number} options.chunkSize - The chunk size for multipart upload.
 * @returns {Promise<Object>} A promise that resolves to upload initialization data.
 * @throws {Error} If the request fails.
 */
export const initMultipartUpload = async (options) => {
  const response = await demoPilotClient.post(`/api/uploads/init`, options);

  if (!response.ok) {
    throw new Error(
      `Failed to init upload with options: ${JSON.stringify(options)}`,
      {
        cause: response,
      },
    );
  }

  return response.data;
};

/**
 * Completes a multipart upload.
 *
 * @param {Object} options - Upload completion data.
 * @param {string} options.uploadId - The upload ID.
 * @param {string} options.objectKey - The object key.
 * @param {Array<Object>} options.parts - Array of upload parts with ETag and PartNumber.
 * @returns {Promise<Object>} A promise that resolves to the completed upload data with URL.
 * @throws {Error} If the request fails.
 */
export const completeMultipartUpload = async (options) => {
  const response = await demoPilotClient.post(`/api/uploads/complete`, options);

  if (!response.ok) {
    throw new Error(
      `Failed to complete upload with options: ${JSON.stringify(options)}`,
      {
        cause: response,
      },
    );
  }

  return response.data;
};

/**
 * Scrapes brand guidelines from a PDF URL.
 *
 * @param {string} projectId - The ID of the project.
 * @param {string} url - The URL to the PDF with brand guidelines.
 * @param {Object} params - Scraping parameters.
 * @returns {Promise<Array>} A promise that resolves to imported assets.
 * @throws {Error} If the request fails.
 */
export const scrapeBrandGuidelines = async (projectId, url, params) => {
  const response = await demoPilotClient.post(`/api/brand-guidelines`, {
    url,
    params,
  });

  if (!response.ok) {
    throw new Error(
      `Failed to scrape brand guidelines for project ${projectId}`,
      { cause: response },
    );
  }

  return response.data;
};

/**
 *
 * @param {Object} params - Query Parameters
 * @param {String} params.q - The search query.
 * @param {Number} params.iterations - How many result pages to request (service default 2; capped at 10).
 * @param {Number} params.retries - Retries per iteration in the upstream package (capped at 8).
 * @param {Boolean} params.moderate - When "true", enables safe-search style moderation in the upstream package.
 * @param {*} options - Optional request options.
 * @returns {Promise<Object>} A promise that resolves to the search results.
 * @throws {Error} If the request fails.
 */
export const searchImages = async (params = {}, options) => {
  const response = await demoPilotClient.get(
    `/api/image-search-duck`,
    params,
    options,
  );

  if (!response.ok) {
    throw new Error(`Failed to search images`, {
      cause: response,
    });
  }

  return response.data;
};
